import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import {
  DataTable,
  type Column,
  Pill,
  EntityStatusPill,
  ProgressBar,
  Avatar,
  Button,
} from "@/design/components";
import { FolderPlus, MessageSquarePlus, Loader2 } from "lucide-react";
import { ApprovalQueue } from "@/screens/approval/ApprovalQueue";
import { AnalyticsSurface } from "@/screens/analytics/AnalyticsSurface";
import { AuditTrail } from "@/screens/audit/AuditTrail";
import { useSeed, useAuditEvents, useVisibleProjects, useProgramme, useOrgIndex, useIsSubContractor, useVisibleSubProjects, useMoney, useActiveProfile, useActiveProgrammeId, useProgrammeLoading } from "@/domain/hooks";
import type { Project, SubProject } from "@/domain/types";
import { ProjectHealthTable } from "./ProjectHealthTable";
import { EvidenceByProject } from "./EvidenceByProject";
import { CreateProjectModal } from "@/components/CreateProjectModal";
import { RequestProjectCreationModal } from "@/components/RequestProjectCreationModal";

function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <WorkspaceShell>
      <div className="px-4 py-5 md:px-8 md:py-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-mute">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
        <div className="mt-6">{children}</div>
      </div>
    </WorkspaceShell>
  );
}

export function WorkspaceProjectsPage() {
  // workspaceId resolved first — every hook below that's workspace-scoped
  // must be threaded with it explicitly rather than falling back to the
  // global activeWorkspaceId store, which a client-side nav into this page
  // can still have pointed at the PREVIOUS workspace on this first render
  // (the store only catches up via WorkspaceShell's effect, which commits
  // after this render). Omitting it here was why the list stayed empty
  // until a full refresh (refresh re-seeds the store from localStorage
  // before the first render, masking the race). Mirrors the fix already
  // applied to programmeId below (ZEUS-141) and the pattern in
  // PublicProjectPage.tsx.
  const { workspaceId } = useParams();
  const projects = useVisibleProjects(workspaceId);
  const orgs = useOrgIndex(workspaceId);
  const money = useMoney();
  const navigate = useNavigate();
  const isSubContractor = useIsSubContractor();
  const mySubs = useVisibleSubProjects(workspaceId);
  const profile = useActiveProfile();
  const loading = useProgrammeLoading(workspaceId);

  // ZEUS-132/133 — UI convenience only; the server is the real boundary
  // (stratus_admin or funder_approver for create, contractor/subcontractor
  // for request — see assertCanCreateProjectDirectly, zeusProject.js).
  // ZEUS-139: uses profile.group directly rather than
  // useCanManageProjectGovernance() — that hook is demo-only (no real-auth
  // branch, see governance.ts's top-of-file note) and resolves to `null` for
  // EVERY real logged-in user, which the old `!== false` fallback treated as
  // "show Create" — so every real contractor/subcontractor saw the
  // stratus-only Create button too (reported live: a real subcontractor
  // user). group is reliable in both demo and real-auth mode (see
  // authStore.ts's USERTYPE_MAP). Funders gained direct creation rights
  // alongside oversight - same authority, no review step.
  const showCreate = profile.group === "oversight" || profile.group === "funder";
  const showRequest = profile.group === "contractor";

  // ZEUS-141 — the real numeric DB programme id, resolved from the
  // workspace slug (workspaceId is a slug like "uganda-fire-stations" for
  // every real user, never a number — see authStore.ts's USERTYPE_MAP
  // landing routes). Passing the raw slug here used to make
  // useCreateZeusProject/useSubmitProjectCreationRequest's toNumericId()
  // silently discard it, so real creates/requests landed with
  // programmeid: NULL on the backend — permanently orphaned from every
  // programme's project list. Demo mode ignores this value entirely (its
  // mutations use the active seed's own programme.id), so resolving it here
  // doesn't affect demo behaviour.
  const programmeId = useActiveProgrammeId(workspaceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  const actions = (showCreate || showRequest) && (
    <>
      {showRequest && (
        <Button variant="secondary" onClick={() => setRequestOpen(true)}>
          <MessageSquarePlus className="h-4 w-4" /> Request new project
        </Button>
      )}
      {showCreate && (
        <Button onClick={() => setCreateOpen(true)}>
          <FolderPlus className="h-4 w-4" /> Create project
        </Button>
      )}
      <CreateProjectModal open={createOpen} onOpenChange={setCreateOpen} programmeId={programmeId} />
      <RequestProjectCreationModal open={requestOpen} onOpenChange={setRequestOpen} programmeId={programmeId} />
    </>
  );

  // Still resolving the programme fetch and nothing to show yet — render a
  // spinner rather than a silently empty table (distinct from "genuinely no
  // projects", which is a real, populated-but-empty state).
  if (loading && projects.length === 0) {
    return (
      <Page title="Projects" subtitle="All projects in this programme, with their delivery health." actions={actions}>
        <div className="flex items-center justify-center py-16 text-mute">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </Page>
    );
  }

  // Governance: a sub-contractor sees only their own work packages, scoped to their budget.
  if (isSubContractor) {
    return (
      <Page
        title="My work packages"
        subtitle="The sub-projects your organisation leads. You see only your own scope and budget."
        actions={actions}
      >
        <SubProjectTable subs={mySubs} projects={projects} navigate={(pid, sid) => navigate(`/workspace/${workspaceId}/projects/${pid}/sub/${sid}`)} />
      </Page>
    );
  }

  const columns: Column<Project>[] = [
    { key: "name", header: "Project", cell: (p) => <div><p className="font-medium text-ink">{p.name}</p><p className="text-xs text-faint">{p.district} · {p.region}</p></div> },
    { key: "contractor", header: "Contractor", cell: (p) => <span className="text-mute">{orgs[p.leadContractorId]?.name}</span> },
    { key: "budget", header: "Budgeted", align: "num", cell: (p) => <span className="text-mute">{money(p.budgeted)}</span> },
    { key: "spent", header: "Spent", cell: (p) => <div className="w-32"><span className="whitespace-nowrap text-sm tabular-nums text-ink">{money(p.spent)}</span><ProgressBar value={p.budgeted ? p.spent / p.budgeted : 0} className="mt-1" /></div> },
    { key: "status", header: "Status", cell: (p) => <EntityStatusPill status={p.status} /> },
  ];
  return (
    <Page title="Projects" subtitle="All projects in this programme, with their delivery health." actions={actions}>
      <DataTable columns={columns} rows={projects} rowKey={(p) => p.id} onRowClick={(p) => navigate(`/workspace/${workspaceId}/projects/${p.id}`)} />
      <section className="mt-8 space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-faint">Project health</h2>
          <p className="mt-1 text-xs text-mute">Every fire station, the contractor behind it, and whether it is on time, on budget and well evidenced.</p>
        </div>
        <ProjectHealthTable projects={projects} onOpen={(p) => navigate(`/workspace/${workspaceId}/projects/${p.id}`)} />
      </section>
    </Page>
  );
}

/** Sub-project-scoped table for sub-contractor governance views. */
export function SubProjectTable({ subs, projects, navigate }: { subs: SubProject[]; projects: Project[]; navigate: (projectId: string, subId: string) => void }) {
  const money = useMoney();
  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? "—";
  const columns: Column<SubProject>[] = [
    { key: "name", header: "Work package", cell: (s) => <div><p className="font-medium text-ink">{s.name}</p><p className="text-xs text-faint">{projectName(s.projectId)}</p></div> },
    { key: "budget", header: "Your budget", align: "num", cell: (s) => <span className="text-mute">{money(s.budgeted)}</span> },
    { key: "spent", header: "Spent", cell: (s) => <div className="w-32"><span className="whitespace-nowrap text-sm tabular-nums text-ink">{money(s.spent)}</span><ProgressBar value={s.budgeted ? s.spent / s.budgeted : 0} className="mt-1" /></div> },
    { key: "evidenced", header: "Evidenced", align: "num", cell: (s) => <span className="text-mute">{money(s.evidenced)}</span> },
    { key: "status", header: "Status", cell: (s) => <EntityStatusPill status={s.status} /> },
  ];
  return <DataTable columns={columns} rows={subs} rowKey={(s) => s.id} onRowClick={(s) => navigate(s.projectId, s.id)} />;
}

export function WorkspaceApprovalsPage() {
  return <Page title="Approvals"><ApprovalQueue audience="Programme" /></Page>;
}

export function WorkspaceMilestonesPage() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const projects = useVisibleProjects(workspaceId);
  const isSubContractor = useIsSubContractor();
  const mySubs = useVisibleSubProjects(workspaceId);

  if (isSubContractor) {
    return (
      <Page title="Your work package health" subtitle="Progress and budget on the sub-projects your organisation leads.">
        <SubProjectTable subs={mySubs} projects={projects} navigate={(pid, sid) => navigate(`/workspace/${workspaceId}/projects/${pid}/sub/${sid}`)} />
      </Page>
    );
  }
  return (
    <Page title="Project health" subtitle="Every fire station, the contractor behind it, and whether it is on time, on budget and well evidenced.">
      <ProjectHealthTable projects={projects} onOpen={(p) => navigate(`/workspace/${workspaceId}/projects/${p.id}`)} />
    </Page>
  );
}

export function WorkspaceEvidencePage() {
  return (
    <Page title="Evidence" subtitle="Evidence grouped by project. Open an item to view the uploaded documents and photos.">
      <EvidenceByProject />
    </Page>
  );
}

const STAGE_LABEL: Record<string, { label: string; variant: "success" | "warn" | "info" | "neutral" }> = {
  active: { label: "Active", variant: "success" },
  awaiting_gate: { label: "Awaiting gate", variant: "warn" },
  in_progress: { label: "Onboarding", variant: "info" },
  invited: { label: "Invited", variant: "neutral" },
  awaiting_invite: { label: "Not invited", variant: "neutral" },
  suspended: { label: "Suspended", variant: "warn" },
};

// KYC verification record · neutral nouns, the tone carries the state.
const KYC_LABEL: Record<string, { label: string; variant: "success" | "warn" | "danger" | "neutral" }> = {
  approved: { label: "Approved", variant: "success" },
  pending_review: { label: "In review", variant: "warn" },
  queried: { label: "Queried", variant: "warn" },
  not_submitted: { label: "Not submitted", variant: "neutral" },
  rejected: { label: "Rejected", variant: "danger" },
};

const ORG_STATUS_LABEL: Record<string, { label: string; variant: "success" | "warn" | "danger" }> = {
  active: { label: "Active", variant: "success" },
  suspended: { label: "Suspended", variant: "warn" },
  revoked: { label: "Revoked", variant: "danger" },
};

export function WorkspaceTeamPage({ embedded = false }: { embedded?: boolean }) {
  const seed = useSeed();
  const contractors = seed.organisations.filter((o) => o.tier === "prime" || o.tier === "sub");
  // KYC softening: government and funder entities carry a lighter verification record, no KYC.
  const entities = seed.organisations.filter((o) => o.tier === "funder" || o.tier === "oversight");
  const primes = contractors.filter((o) => o.tier === "prime");
  const subs = contractors.filter((o) => o.tier === "sub");
  const reportersOf = (orgId: string) => seed.people.filter((p) => p.organisationId === orgId && /reporter/i.test(p.role));

  const body = (
    <>
      <DataTable
        columns={[
          { key: "name", header: "Organisation", cell: (o) => <span className="font-medium text-ink">{o.name}</span> },
          { key: "tier", header: "Tier", cell: (o) => <Pill variant={o.tier === "prime" ? "brand" : "neutral"}>{o.tier === "prime" ? "Prime" : "Sub"}</Pill> },
          { key: "contact", header: "Contact", cell: (o) => { const p = seed.people.find((x) => x.id === o.primaryContactPersonId); return p ? <div className="flex items-center gap-2"><Avatar initials={p.avatarInitials} size={22} /><span className="text-mute">{p.fullName}</span></div> : <span className="text-faint">—</span>; } },
          { key: "kyc", header: "KYC", cell: (o) => { const k = KYC_LABEL[o.kycState] ?? { label: o.kycState, variant: "neutral" as const }; return <Pill variant={k.variant} dot>{k.label}</Pill>; } },
          { key: "status", header: "Status", cell: (o) => { const s = ORG_STATUS_LABEL[o.status] ?? { label: o.status, variant: "neutral" as const }; return <Pill variant={s.variant} dot>{s.label}</Pill>; } },
        ]}
        rows={contractors}
        rowKey={(o) => o.id}
      />

      {/* Onboarding cascade — read-only, makes the single-prime-with-subs structure legible. */}
      <section className="mt-8">
        <h2 className="mb-1 text-sm font-semibold text-ink">Onboarding cascade</h2>
        <p className="mb-3 text-xs text-mute">The prime invites its subcontractors; each sub onboards its own field reporters. We stay in control of what happens below us.</p>
        <div className="space-y-2">
          {primes.map((prime) => (
            <div key={prime.id} className="rounded-lg border border-line bg-card p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">{prime.name} <span className="text-xs text-faint">· prime</span></span>
                <Pill variant={STAGE_LABEL[prime.pipelineStage ?? "active"]?.variant ?? "neutral"}>{STAGE_LABEL[prime.pipelineStage ?? "active"]?.label ?? "Active"}</Pill>
              </div>
              <ul className="mt-2 space-y-1.5 border-l-2 border-hairline pl-3">
                {subs.map((sub) => {
                  const reps = reportersOf(sub.id);
                  return (
                    <li key={sub.id}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-ink">{sub.name}</span>
                        <Pill variant={STAGE_LABEL[sub.pipelineStage ?? "active"]?.variant ?? "neutral"}>{STAGE_LABEL[sub.pipelineStage ?? "active"]?.label ?? "Active"}</Pill>
                      </div>
                      {reps.length > 0 && (
                        <p className="mt-0.5 text-[11px] text-faint">Field reporters: {reps.map((r) => r.fullName).join(", ")}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* KYC softening: government and funder entities carry a verification record, not KYC. */}
      <section className="mt-8">
        <h2 className="mb-1 text-sm font-semibold text-ink">Government and funder entities</h2>
        <p className="mb-3 text-xs text-mute">Public bodies are not subject to contractor KYC; we hold a lighter verification record of their registered identity and role in the deal.</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {entities.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded-md border border-line bg-card px-3 py-2">
              <div>
                <p className="text-sm font-medium text-ink">{o.name}</p>
                <p className="text-[11px] text-faint capitalize">{o.tier}</p>
              </div>
              <Pill variant="success">Verified entity</Pill>
            </div>
          ))}
        </div>
      </section>
    </>
  );

  return embedded ? body : <Page title="Participants" subtitle="Contractors, the people involved, and the onboarding cascade beneath the prime.">{body}</Page>;
}

export function WorkspaceAnalyticsPage() {
  const programme = useProgramme();
  return <Page title="Analytics"><AnalyticsSurface scopeLabel={programme.name} /></Page>;
}

export function WorkspaceAuditPage() {
  return <Page title="Audit trail"><AuditTrail events={useAuditEvents()} /></Page>;
}
